// ============================================================================
// Schema Compiler — schema.json → Offsets + Hash + Codegen
// ============================================================================
import { ERROR_REGION_SIZE } from '../types.js';
import { computeParamLayout } from '../memory/param-buffer.js';
// --- CRC-32 ---
const CRC_TABLE = new Uint32Array(256);
for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++)
        c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    CRC_TABLE[i] = c;
}
export function crc32(str) {
    const bytes = new TextEncoder().encode(str);
    let crc = 0xFFFFFFFF;
    for (const b of bytes)
        crc = CRC_TABLE[(crc ^ b) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
}
export function computeSchemaHash(schema) {
    const params = Object.entries(schema.parameters)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([n, f]) => `${n}:${f.type}`)
        .join(',');
    const cmds = schema.commands.sort((a, b) => a.id - b.id).map(c => `${c.id}:${c.name}`).join(',');
    // Include access_groups in the hash — they affect memory layout (cache-line alignment
    // and field ordering), so changing them changes the byte offsets of parameters.
    const groups = schema.access_groups
        ? schema.access_groups.map(g => g.join('+')).join(';')
        : '';
    // Include ring_buffer config — capacity and slot_size affect control SAB layout.
    const ring = schema.ring_buffer
        ? `${schema.ring_buffer.capacity ?? ''},${schema.ring_buffer.slot_size ?? ''}`
        : '';
    return crc32(`v${schema.version}|${params}|${cmds}|g:${groups}|r:${ring}`);
}
export function compileSchema(schema) {
    // --- Schema validation ---
    // Check for empty parameter names
    for (const name of Object.keys(schema.parameters)) {
        if (!name || name.trim().length === 0) {
            throw new Error('Schema contains an empty parameter name.');
        }
    }
    // Check for duplicate command IDs
    const cmdIds = new Set();
    for (const cmd of schema.commands) {
        if (cmdIds.has(cmd.id)) {
            throw new Error(`Duplicate command ID ${cmd.id} for command "${cmd.name}". Each command must have a unique ID.`);
        }
        cmdIds.add(cmd.id);
    }
    // Check for duplicate command names
    const cmdNames = new Set();
    for (const cmd of schema.commands) {
        if (cmdNames.has(cmd.name)) {
            throw new Error(`Duplicate command name "${cmd.name}". Each command must have a unique name.`);
        }
        cmdNames.add(cmd.name);
    }
    // Check for Rust codegen name collisions (camelCase → PascalCase can merge)
    const rustEnumNames = new Set();
    for (const cmd of schema.commands) {
        const rustName = cmd.name.split('_').map(w => w[0]?.toUpperCase() + w.slice(1).toLowerCase()).join('');
        if (rustEnumNames.has(rustName)) {
            throw new Error(`Command names collide after Rust codegen: "${cmd.name}" produces enum variant "${rustName}" ` +
                `which already exists. Rename one of the conflicting commands.`);
        }
        rustEnumNames.add(rustName);
    }
    // Check that access_groups reference valid parameter names
    if (schema.access_groups) {
        for (const group of schema.access_groups) {
            for (const name of group) {
                if (!(name in schema.parameters)) {
                    throw new Error(`access_groups references unknown parameter "${name}".`);
                }
            }
        }
    }
    const paramLayout = computeParamLayout(schema.parameters, schema.access_groups);
    const hash = computeSchemaHash(schema);
    let paramBufferSize = ERROR_REGION_SIZE;
    if (paramLayout.length > 0) {
        const last = paramLayout[paramLayout.length - 1];
        paramBufferSize = last.offset + last.byteSize;
    }
    paramBufferSize = Math.ceil(paramBufferSize / 8) * 8;
    return {
        hash,
        paramLayout,
        commands: schema.commands,
        ringCapacity: schema.ring_buffer?.capacity ?? 256,
        slotSize: schema.ring_buffer?.slot_size ?? 32,
        paramBufferSize,
    };
}
// --- Codegen: TypeScript ---
export function codegenTypeScript(compiled) {
    const hex = compiled.hash.toString(16).toUpperCase().padStart(8, '0');
    const cmdIds = compiled.commands.map(c => c.id);
    const lines = [
        `// Auto-generated by @alloyhead/core schema compiler. Do not edit.`,
        `// Schema hash: 0x${hex}`,
        ``,
        `export const SCHEMA_HASH = 0x${hex};`,
        ``,
        `export const PARAM = {`,
        ...compiled.paramLayout.map(f => `  ${f.name}: ${f.offset},  // ${f.type}`),
        `} as const;`,
        ``,
        `export const CMD = {`,
        ...compiled.commands.map(c => `  ${c.name}: ${c.id},`),
        `} as const;`,
        ``,
        `export type ParamKey = keyof typeof PARAM;`,
        `export type CmdKey = keyof typeof CMD;`,
        ``,
        `/** Numeric union of all valid command IDs — use as parameter type for pushCommand. */`,
        `export type CmdType = ${cmdIds.length > 0 ? cmdIds.join(' | ') : 'never'};`,
    ];
    return lines.join('\n');
}
// --- Codegen: Rust ---
export function codegenRust(compiled) {
    const hex = compiled.hash.toString(16).toUpperCase().padStart(8, '0');
    const rustTypes = {
        Uint8: 'u8', Uint16: 'u16', Uint32: 'u32',
        Int8: 'i8', Int16: 'i16', Int32: 'i32',
        Float32: 'f32', Float64: 'f64',
    };
    const lines = [
        `// Auto-generated by @alloyhead/core schema compiler. Do not edit.`,
        ``,
        `pub const SCHEMA_HASH: u32 = 0x${hex};`,
        `pub const ERROR_REGION_SIZE: usize = ${ERROR_REGION_SIZE};`,
        ``,
        `#[repr(C)]`,
        `pub struct ParamBuffer {`,
        `    pub _error_region: [u8; ${ERROR_REGION_SIZE}],`,
    ];
    let cursor = ERROR_REGION_SIZE;
    let padIdx = 0;
    for (const f of compiled.paramLayout) {
        const pad = f.offset - cursor;
        if (pad > 0)
            lines.push(`    pub _pad${padIdx++}: [u8; ${pad}],`);
        const name = f.name.replace(/([A-Z])/g, '_$1').toLowerCase();
        lines.push(`    pub ${name}: ${rustTypes[f.type]},`);
        cursor = f.offset + f.byteSize;
    }
    lines.push(`}`, ``, `#[repr(u16)]`, `#[derive(Debug, Clone, Copy, PartialEq)]`, `pub enum Cmd {`);
    for (const c of compiled.commands) {
        const name = c.name.split('_').map(w => w[0].toUpperCase() + w.slice(1).toLowerCase()).join('');
        lines.push(`    ${name} = ${c.id},`);
    }
    lines.push(`}`);
    return lines.join('\n');
}
//# sourceMappingURL=compiler.js.map